import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Award, Clock, User, ChevronRight, Save, History,
  CheckCircle, AlertCircle, XCircle, ChevronDown, ChevronUp, Pencil, Trash2, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import { exigeTripartite } from '../utils/autonomie';

const CATEGORIES = {
  'VNM': [
    'Encadrement avec 2 VNM + tuteur',
    'Encadrement avec 2 VNM en autonomie',
    'Encadrement avec 4 VNM + tuteur',
    'Encadrement avec 4 VNM en autonomie'
  ],
  'Engins tractés': [
    'Engins tractés avec tuteur',
    'Engins tractés en autonomie'
  ],
  'PNM': [
    'PNM avec tuteur',
    'PNM en autonomie'
  ],
  'Flyboard': [
    'Flyboard avec tuteur',
    'Flyboard en autonomie'
  ],
  'Conduite bateau': [
    'Conduite bateau avec tuteur',
    'Conduite bateau en autonomie'
  ]
};

const CRITERES = [
  'Sécurité',
  'Briefing',
  'Prise en charge des publics',
  'Respect de la réglementation',
  'Adaptation',
  'Animation',
  'Parcours/timing'
];

const STATUTS = [
  { key: 'non_competent', label: 'Non compétent', color: 'red', Icon: XCircle },
  { key: 'en_cours', label: 'En cours', color: 'orange', Icon: AlertCircle },
  { key: 'competent', label: 'Compétent', color: 'green', Icon: CheckCircle }
];

const StatutBadge = ({ statut, small }) => {
  const c = {
    competent: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200', label: 'Compétent' },
    en_cours: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200', label: 'En cours' },
    non_competent: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Non compétent' }
  }[statut] || { bg: 'bg-slate-100', text: 'text-slate-500', border: 'border-slate-200', label: 'Non évalué' };
  return (
    <span className={`inline-flex items-center ${small ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm'} rounded-full border ${c.bg} ${c.text} ${c.border} font-medium`}>
      {c.label}
    </span>
  );
};

const SessionActions = ({ session, onEdit, onDelete }) => (
  session.motif_blocage ? (
    <div className="flex items-center px-3 text-slate-400" title={session.motif_blocage} data-testid={`session-lock-${session.session_id}`}>
      <Lock className="h-4 w-4" />
    </div>
  ) : (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-1 px-1 sm:px-2 border-l border-slate-100">
      <button type="button" onClick={() => onEdit(session)} title="Modifier" className="p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#0F172A]" data-testid={`session-edit-${session.session_id}`}>
        <Pencil className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onDelete(session)} title="Supprimer" className="p-2 rounded-md text-red-500 hover:bg-red-50" data-testid={`session-delete-${session.session_id}`}>
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
);

const Competences = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [categorie, setCategorie] = useState('VNM');
  const [sousCategorie, setSousCategorie] = useState('');

  // Form state
  const [heures, setHeures] = useState(1);
  const [dateSession, setDateSession] = useState(new Date().toISOString().split('T')[0]);
  const [criteres, setCriteres] = useState({});
  const [statutGlobal, setStatutGlobal] = useState('');
  const [appreciation, setAppreciation] = useState('');
  const [saving, setSaving] = useState(false);

  // History
  const [historique, setHistorique] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  // Session en cours de modification (null = nouvelle saisie)
  const [editing, setEditing] = useState(null);

  // Cascade: last statuts for each sous-categorie
  const [lastStatuts, setLastStatuts] = useState({});

  useEffect(() => {
    loadStagiaires();
  }, []);

  useEffect(() => {
    setEditing(null);
    if (selectedStagiaire) {
      loadHistorique();
    }
  }, [selectedStagiaire]);

  // Reset sous-categorie when category changes
  useEffect(() => {
    setSousCategorie('');
    resetForm();
  }, [categorie]);

  // Reset form when sous-categorie changes (ou pré-remplissage en mode modification)
  useEffect(() => {
    if (editing && editing.sous_categorie === sousCategorie) {
      setCriteres(editing.criteres || {});
      setStatutGlobal(editing.statut_global || '');
      setAppreciation(editing.appreciation || '');
      setHeures(editing.heures_encadrement ?? 0);
      setDateSession((editing.date_session || '').slice(0, 10));
      return;
    }
    if (editing) setEditing(null);
    resetForm();
  }, [sousCategorie, editing]);

  const startEdit = (session) => {
    setEditing(session);
    setSousCategorie(session.sous_categorie);
    setTimeout(() => document.querySelector('[data-testid="evaluation-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleDelete = async (session) => {
    const nom = `${currentStagiaire?.user_info?.prenom || ''} ${currentStagiaire?.user_info?.nom || ''}`.trim();
    const date = new Date(session.date_session).toLocaleDateString('fr-FR');
    if (!window.confirm(`Supprimer l'évaluation de ${nom} du ${date} — ${session.sous_categorie} ?\nLa note et les ${session.heures_encadrement || 0}h d'encadrement de cette session seront effacées.`)) return;
    try {
      await axios.delete(`/evaluations-entreprise/${session.session_id}`);
      toast.success('Évaluation supprimée');
      if (editing?.session_id === session.session_id) setEditing(null);
      loadHistorique();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setDateSession(new Date().toISOString().split('T')[0]);
    setCriteres({});
    setStatutGlobal('');
    setAppreciation('');
    setHeures(1);
  };

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data);
      if (response.data.length > 0) {
        setSelectedStagiaire(response.data[0].user_id);
      }
    } catch (error) {
    }
  };

  const loadHistorique = async () => {
    try {
      const response = await axios.get(`/evaluations-entreprise/stagiaire/${selectedStagiaire}`);
      setHistorique(response.data);

      // Build cascade statuts map (latest per sous-cat)
      const statuts = {};
      response.data.forEach(s => {
        const key = `${s.categorie}__${s.sous_categorie}`;
        if (!statuts[key]) statuts[key] = s.statut_global;
      });
      setLastStatuts(statuts);
    } catch (error) {
    }
  };

  const isSousCategorieUnlocked = (cat, idx) => {
    if (idx === 0) return true;
    const prev = (CATEGORIES[cat] || [])[idx - 1];
    if (!prev) return true;
    return lastStatuts[`${cat}__${prev}`] === 'competent';
  };

  const handleSave = async () => {
    if (!selectedStagiaire || !sousCategorie || !statutGlobal) {
      toast.error('Veuillez sélectionner une sous-catégorie et un statut global');
      return;
    }

    setSaving(true);
    try {
      const corps = { heures_encadrement: parseFloat(heures) || 0, date_session: dateSession, criteres, statut_global: statutGlobal, appreciation };
      if (editing) {
        await axios.put(`/evaluations-entreprise/${editing.session_id}`, corps);
        toast.success('Évaluation modifiée');
        setEditing(null);
      } else {
        await axios.post('/evaluations-entreprise', { stagiaire_id: selectedStagiaire, categorie, sous_categorie: sousCategorie, ...corps });
        toast.success('Évaluation enregistrée ! Notifications envoyées.');
      }
      resetForm();
      loadHistorique();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  // Hours per sous-categorie from history
  const heuresParSousCategorie = {};
  historique.forEach(s => {
    const key = `${s.categorie}__${s.sous_categorie}`;
    if (!heuresParSousCategorie[key]) heuresParSousCategorie[key] = 0;
    heuresParSousCategorie[key] += s.heures_encadrement || 0;
  });

  // Hours per category from history
  const heuresParCategorie = {};
  historique.forEach(s => {
    if (!heuresParCategorie[s.categorie]) heuresParCategorie[s.categorie] = 0;
    heuresParCategorie[s.categorie] += s.heures_encadrement || 0;
  });

  // History filtered for current category
  const historiqueFiltre = historique.filter(s => s.categorie === categorie);

  const canSave = selectedStagiaire && sousCategorie && statutGlobal;

  return (
    <div className="space-y-6" data-testid="competences-page">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Évaluation des Compétences</h1>
        <p className="text-slate-600">Évaluez et validez les compétences de vos stagiaires par session</p>
      </div>

      {/* Sticky Stagiaire Banner - visible au scroll */}
      {currentStagiaire && (
        <div className="sticky top-0 z-30 bg-[#0F172A] text-white rounded-lg px-5 py-3 shadow-lg flex items-center justify-between" data-testid="sticky-stagiaire-banner">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#8DC63F]/20 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-[#8DC63F]">
                {currentStagiaire.user_info?.prenom?.[0]}{currentStagiaire.user_info?.nom?.[0]}
              </span>
            </div>
            <div>
              <p className="font-semibold text-sm">
                {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
              </p>
              <p className="text-xs text-slate-400">{currentStagiaire.centre_formation} — {categorie}{sousCategorie ? ` / ${sousCategorie}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {Object.entries(heuresParCategorie).map(([cat, h]) => (
              <div key={cat} className={`text-center px-2.5 py-1 rounded-md text-xs ${cat === categorie ? 'bg-[#8DC63F]/20 text-[#8DC63F]' : 'bg-white/10 text-white/70'}`}>
                <span className="font-bold">{h}h</span> <span className="hidden sm:inline">{cat}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sélection Stagiaire + Catégorie */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="step-selection">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="stagiaire">Stagiaire</Label>
            <select
              id="stagiaire"
              value={selectedStagiaire}
              onChange={(e) => setSelectedStagiaire(e.target.value)}
              className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-stagiaire"
            >
              {stagiaires.length === 0 ? (
                <option value="">Aucun stagiaire attribué</option>
              ) : (
                stagiaires.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="categorie">Catégorie</Label>
            <select
              id="categorie"
              value={categorie}
              onChange={(e) => setCategorie(e.target.value)}
              className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-categorie"
            >
              {Object.keys(CATEGORIES).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Sous-catégorie avec cascade */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="step-sous-categorie">
        <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <ChevronRight className="h-5 w-5 text-[#8DC63F]" />
          Sous-catégorie - {categorie}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(CATEGORIES[categorie] || []).map((sc, idx) => {
            const isUnlocked = isSousCategorieUnlocked(categorie, idx);
            const isSelected = sousCategorie === sc;
            const lastStatus = lastStatuts[`${categorie}__${sc}`];
            const scHeures = heuresParSousCategorie[`${categorie}__${sc}`] || 0;

            return (
              <button
                key={sc}
                onClick={() => isUnlocked && setSousCategorie(sc)}
                disabled={!isUnlocked}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  !isUnlocked
                    ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'border-[#8DC63F] bg-[#8DC63F]/5 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-[#8DC63F]/50 cursor-pointer'
                }`}
                data-testid={`sous-cat-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className={`font-medium text-sm ${isUnlocked ? 'text-[#0F172A]' : 'text-slate-400'}`}>
                    {sc}
                  </span>
                  <div className="flex items-center gap-2">
                    {scHeures > 0 && (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs rounded-full font-semibold">
                        {scHeures}h
                      </span>
                    )}
                    {lastStatus && <StatutBadge statut={lastStatus} small />}
                    {!isUnlocked && (
                      <span className="text-xs text-slate-400 bg-slate-200 px-2 py-0.5 rounded">Verrouillé</span>
                    )}
                  </div>
                </div>
                <p className={`mt-1.5 text-xs ${exigeTripartite(categorie, sc) ? 'text-purple-600 font-medium' : 'text-slate-500'}`} data-testid={`sous-cat-circuit-${idx}`}>
                  {exigeTripartite(categorie, sc) ? 'Validation tripartite : tuteur → entreprise → stagiaire' : 'Validation par le tuteur uniquement'}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Formulaire d'évaluation (visible quand sous-catégorie sélectionnée) */}
      {sousCategorie && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="evaluation-form">
          {/* Header sombre */}
          <div className="bg-[#0F172A] text-white px-6 py-4">
            <h2 className="text-lg font-semibold">Session d'évaluation : {sousCategorie}</h2>
            <p className="text-sm text-slate-300">{categorie}</p>
          </div>
          {editing && (
            <div className="flex flex-wrap items-center gap-3 bg-amber-50 border-b border-amber-200 px-6 py-3" data-testid="edit-mode-banner">
              <Pencil className="h-4 w-4 text-amber-600" />
              <span className="text-sm text-amber-800 flex-1 min-w-0">
                Modification de la session du <strong>{new Date(editing.date_session).toLocaleDateString('fr-FR')}</strong>
              </span>
              <button type="button" onClick={() => setEditing(null)} className="text-sm font-medium text-amber-700 hover:underline" data-testid="cancel-edit-btn">
                Annuler la modification
              </button>
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Date + Heures */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Date de la session
                </Label>
                <input
                  type="date"
                  value={dateSession}
                  onChange={(e) => setDateSession(e.target.value)}
                  className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                  data-testid="eval-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-500" />
                  Heures d'encadrement
                </Label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={heures}
                  onChange={(e) => setHeures(e.target.value)}
                  className="w-full h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                  data-testid="eval-heures"
                />
              </div>
            </div>

            {/* Évaluation des critères */}
            <div>
              <h3 className="text-sm font-semibold text-[#0F172A] mb-3 uppercase tracking-wide">
                Évaluation des critères
              </h3>
              <div className="space-y-2">
                {CRITERES.map((critere) => (
                  <div
                    key={critere}
                    className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-[#0F172A]">{critere}</span>
                    <div className="flex gap-1.5">
                      {STATUTS.map(({ key, label, color }) => {
                        const isSelected = criteres[critere] === key;
                        const colors = {
                          red: isSelected ? 'bg-red-500 text-white border-red-500' : 'border-slate-200 text-slate-500 hover:border-red-300',
                          orange: isSelected ? 'bg-orange-500 text-white border-orange-500' : 'border-slate-200 text-slate-500 hover:border-orange-300',
                          green: isSelected ? 'bg-green-500 text-white border-green-500' : 'border-slate-200 text-slate-500 hover:border-green-300'
                        };
                        return (
                          <button
                            key={key}
                            onClick={() => setCriteres(prev => ({ ...prev, [critere]: key }))}
                            className={`px-2.5 py-1 text-xs rounded-md border transition-all ${colors[color]}`}
                            data-testid={`critere-${critere}-${key}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-slate-200" />

            {/* Statut global */}
            <div>
              <h3 className="text-sm font-semibold text-[#0F172A] mb-3 uppercase tracking-wide">
                Statut global de la compétence
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {STATUTS.map(({ key, label, color, Icon }) => {
                  const isSelected = statutGlobal === key;
                  const ringMap = {
                    red: isSelected ? 'bg-red-50 border-red-500 ring-2 ring-red-200' : 'border-slate-200 hover:border-red-300',
                    orange: isSelected ? 'bg-orange-50 border-orange-500 ring-2 ring-orange-200' : 'border-slate-200 hover:border-orange-300',
                    green: isSelected ? 'bg-green-50 border-green-500 ring-2 ring-green-200' : 'border-slate-200 hover:border-green-300'
                  };
                  const textMap = {
                    red: isSelected ? 'text-red-700' : 'text-slate-600',
                    orange: isSelected ? 'text-orange-700' : 'text-slate-600',
                    green: isSelected ? 'text-green-700' : 'text-slate-600'
                  };
                  return (
                    <button
                      key={key}
                      onClick={() => setStatutGlobal(key)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all cursor-pointer ${ringMap[color]}`}
                      data-testid={`statut-global-${key}`}
                    >
                      <Icon className={`h-6 w-6 ${textMap[color]}`} />
                      <span className={`text-sm font-semibold ${textMap[color]}`}>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Appréciation */}
            <div className="space-y-2">
              <Label>Appréciation (optionnel)</Label>
              <textarea
                value={appreciation}
                onChange={(e) => setAppreciation(e.target.value)}
                placeholder="Commentaire sur la session, points à améliorer, observations..."
                className="w-full h-24 px-3 py-2 text-sm border border-slate-200 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                data-testid="eval-appreciation"
              />
            </div>

            {/* Bouton enregistrer */}
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-slate-500">
                {CRITERES.filter(c => !criteres[c]).length > 0 && (
                  <span className="text-orange-600">
                    {CRITERES.filter(c => !criteres[c]).length} critère(s) non évalué(s)
                  </span>
                )}
              </div>
              <Button
                onClick={handleSave}
                disabled={!canSave || saving}
                className={canSave ? 'bg-[#8DC63F] hover:bg-[#7db336] text-white' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}
                data-testid="save-evaluation-btn"
              >
                {saving ? 'Enregistrement...' : (
                  <><Save className="h-4 w-4 mr-2" />{editing ? 'Enregistrer les modifications' : 'Enregistrer la session'}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Historique */}
      {selectedStagiaire && historiqueFiltre.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="evaluation-historique">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
            <History className="h-5 w-5 text-[#8DC63F]" />
            <h2 className="text-lg font-semibold text-[#0F172A]">
              Historique - {categorie}
            </h2>
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
              {historiqueFiltre.length} session(s)
            </span>
          </div>
          <div className="px-6 py-4 space-y-3">
            {historiqueFiltre.map((session) => (
              <div key={session.session_id} className={`border rounded-lg overflow-hidden ${editing?.session_id === session.session_id ? 'border-amber-400 ring-2 ring-amber-100' : 'border-slate-200'}`}>
                <div className="flex items-stretch">
                <button
                  onClick={() => setExpandedSession(expandedSession === session.session_id ? null : session.session_id)}
                  className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 sm:p-4 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`session-${session.session_id}`}
                >
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="text-center min-w-[52px] sm:min-w-[60px] shrink-0">
                      <p className="text-lg font-bold text-[#0F172A]">{session.heures_encadrement}h</p>
                      <p className="text-xs text-slate-500">encadrement</p>
                    </div>
                    <div className="border-l border-slate-200 pl-3 sm:pl-4 min-w-0">
                      <p className="font-medium text-[#0F172A] text-sm break-words">{session.sous_categorie}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(session.date_session).toLocaleDateString('fr-FR')} - Par {session.evaluateur_nom || 'Tuteur'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <StatutBadge statut={session.statut_global} small />
                    {expandedSession === session.session_id
                      ? <ChevronUp className="h-4 w-4 text-slate-400" />
                      : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </button>
                <SessionActions session={session} onEdit={startEdit} onDelete={handleDelete} />
                </div>
                {expandedSession === session.session_id && (
                  <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50 pt-3 space-y-3">
                    {session.motif_blocage && (
                      <p className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`session-lock-motif-${session.session_id}`}>
                        <Lock className="h-3.5 w-3.5" /> {session.motif_blocage}
                      </p>
                    )}
                    {session.criteres && Object.keys(session.criteres).length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-600 mb-2 uppercase">Critères évalués</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Object.entries(session.criteres).map(([c, s]) => (
                            <div key={c} className="flex items-center justify-between bg-white p-2 rounded border border-slate-100">
                              <span className="text-xs text-slate-700">{c}</span>
                              <StatutBadge statut={s} small />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {session.appreciation && (
                      <div className="bg-white p-3 rounded border border-slate-100">
                        <p className="text-xs font-semibold text-slate-600 mb-1">Appréciation</p>
                        <p className="text-sm text-slate-700 italic">"{session.appreciation}"</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Récapitulatif des heures */}
      {Object.keys(heuresParCategorie).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="heures-recap">
          <h2 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#8DC63F]" />
            Récapitulatif des heures
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.keys(CATEGORIES).map(cat => (
              <div
                key={cat}
                className={`p-4 rounded-lg border-2 text-center ${
                  cat === categorie ? 'border-[#8DC63F] bg-[#8DC63F]/5' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p className="text-2xl font-bold text-[#0F172A]">{heuresParCategorie[cat] || 0}h</p>
                <p className="text-xs text-slate-600">{cat}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Competences;
